import React from 'react';
import PropTypes from 'prop-types';
import PgIcon from './pg-icon';
import PgNavItem from './pg-nav-item';

const navItemShape = PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    icon: PropTypes.string,
    href: PropTypes.string,
    hasSubmenu: PropTypes.bool,
    onClick: PropTypes.func,
    actionIcons: PropTypes.array,
});

const renderItems = (items, activeId, onItemClick) => items.map((item) => (
    <PgNavItem
        key={item.id}
        icon={item.icon}
        label={item.label}
        href={item.href}
        active={item.id === activeId}
        hasSubmenu={item.hasSubmenu}
        actionIcons={item.actionIcons}
        onClick={(e) => {
            item.onClick?.(e);
            onItemClick?.(item.id);
        }}
    />
));

/**
 * Renders a `PgNavItem` list — either a flat `.pg-top-nav` row group (pass
 * `items`) or a `.pg-nav-sections` block of optionally-collapsible,
 * optionally-headed groups (pass `sections`). This is the piece `PgSidebar`
 * uses for both its fixed top nav and its scrollable middle zone.
 */
const PgNavMenuItems = ({
    items,
    sections,
    activeId,
    onItemClick,
    collapsedSections,
    onSectionToggle,
    className,
}) => {
    if (sections.length > 0) {
        return (
            <div className={['pg-nav-sections', className].filter(Boolean).join(' ')}>
                {sections.map((group, idx) => {
                    const sectionId = group.section
                        ? group.section.toLowerCase().replace(/\s+/g, '-')
                        : `section-${idx}`;
                    const isCollapsed = !!collapsedSections[sectionId];

                    return (
                        <div key={sectionId} className={`pg-nav-section${isCollapsed ? ' is-collapsed' : ''}`}>
                            {group.section && (
                                <div
                                    className="pg-nav-section-header"
                                    onClick={() => onSectionToggle?.(sectionId)}
                                >
                                    <div className="pg-nav-sec">{group.section}</div>
                                    <span className="pg-nav-section-chevron">
                                        <PgIcon name="chevron-down" size={14} />
                                    </span>
                                </div>
                            )}
                            <div className="pg-nav-section-content">
                                <div className="pg-top-nav">
                                    {renderItems(group.items || [], activeId, onItemClick)}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div className={['pg-top-nav', className].filter(Boolean).join(' ')}>
            {renderItems(items, activeId, onItemClick)}
        </div>
    );
};

PgNavMenuItems.propTypes = {
    /** Flat row list — rendered as a single `.pg-top-nav` group with no section headers. Ignored when `sections` is non-empty. */
    items: PropTypes.arrayOf(navItemShape),
    /** Grouped rows: `[{ section?: string, items: [...] }]`. A group without `section` renders header-less (its id falls back to `section-{index}`); a group with `section` gets a click-to-collapse header. Takes priority over `items` when non-empty. */
    sections: PropTypes.arrayOf(PropTypes.shape({
        section: PropTypes.string,
        items: PropTypes.arrayOf(navItemShape),
    })),
    /** Item id considered active — passed through to each `PgNavItem`'s `active` prop. */
    activeId: PropTypes.string,
    /** Called with an item's `id` after that item's own `onClick` (if any) runs. */
    onItemClick: PropTypes.func,
    /** `{ [sectionId]: boolean }` — which section groups render collapsed. */
    collapsedSections: PropTypes.object,
    /** Called with a section's id when its header is clicked. Receives no collapsed value — the caller owns that state (see `PgSidebar`). */
    onSectionToggle: PropTypes.func,
    className: PropTypes.string,
};

PgNavMenuItems.defaultProps = {
    items: [],
    sections: [],
    activeId: undefined,
    onItemClick: undefined,
    collapsedSections: {},
    onSectionToggle: undefined,
    className: '',
};

export default PgNavMenuItems;
